import React from "react";
import { Link } from "react-router-dom";
import { formatToShortHand } from "../../helpers/common_helper";
import { formatDateShort, formatDateSort, formatCurrency } from "./helpers";

/**
 * Transforms a single merchant/store for table display (active stores)
 * @param {Object} merchant - Merchant object from API
 * @param {Function} navigateToMerchantDetails - Navigation handler function
 * @returns {Object} Transformed merchant object
 */
export const transformMerchantItem = (merchant, navigateToMerchantDetails, rowIndex) => {
    const createdSortValue = merchant?.created_at ? new Date(merchant.created_at).getTime() : 0;

    return {
        sn: rowIndex + 1,
        store_name: merchant.store_name,
        __store_domain_sort: merchant?.store_domain ? merchant.store_domain.toLowerCase() : '',
        store_domain: (
            <a
                href={`https://${merchant.store_domain}`}
                target="_blank"
                rel="noreferrer">
                {merchant.store_domain}
            </a>
        ),
        link: (
            <span
                onClick={() => navigateToMerchantDetails(merchant)}
                style={{ cursor: 'pointer', color: '#007bff' }}
            >
                <i className="fas fa-eye"></i> Details
            </span>
        ),
        plan: merchant.plan ? (
            <>
                <strong>{merchant.plan} ({merchant.plan_status})</strong> <br />
                <em>{formatDateSort(merchant?.current_period_start)} to {formatDateSort(merchant?.current_period_end)}</em>
            </>
        ) : (
            "Not Updated"
        ),
        recovered_revenue: (
            <span>${formatToShortHand(parseInt(merchant.recovered_revenue))}</span>
        ),
        created: <span>{formatDateShort(merchant.created_at)}</span>,
        __created_sort: createdSortValue,
        is_set_webpixel: merchant?.is_set_webpixel ? "Yes" : "No",
        coupon_scanner: (
            <Link
                to={`/coupon-scanner?store=${encodeURIComponent(merchant.store_domain)}`}
                state={{ store: merchant.store_domain }}
            >
                <i className="fas fa-eye"></i> View
            </Link>
        ),
    };
};

/**
 * Transforms a single uninstalled merchant/store for table display
 * @param {Object} merchant - Merchant object from API
 * @param {Function} navigateToMerchantDetails - Navigation handler function
 * @returns {Object} Transformed merchant object
 */
export const transformUninstalledMerchantItem = (merchant, navigateToMerchantDetails) => {
    const formatDateCustom = (dateString) => {
        if (!dateString) return "Not Updated";
        const date = new Date(dateString);
        return `${date.getDate()}/${date.getMonth()}/${date.getFullYear()}`;
    };

    return {
        store_name: merchant.store_name,
        store_domain: (
            <a href={`https://${merchant.store_domain}`} target="_blank" rel="noreferrer">
                {merchant.store_domain}
            </a>
        ),
        link: (
            <span
                onClick={() => navigateToMerchantDetails(merchant)}
                style={{ cursor: 'pointer', color: '#007bff' }}
            >
                <i className="fas fa-eye"></i> Details
            </span>
        ),
        transactions: merchant.total_orders,
        sales: formatCurrency(merchant.total_cost, '$', 2),
        discount: formatCurrency(merchant.total_discount, '$', 2),
        revenue: formatCurrency(merchant.total, '$', 2),
        created: formatDateCustom(merchant.created_at),
        script: merchant.update_script ? formatDateCustom(merchant.update_script) : "Not Updated",
        theme: merchant?.storefront ? (merchant.storefront == '2.0' ? 'UI Block' : "SDK") : "Not Updated",
    };
};

/**
 * Transforms array of merchants into DataTable format (active stores)
 * @param {Array} merchants - Array of merchant objects from API
 * @param {Array} columns - Table column configuration
 * @param {Function} navigateToMerchantDetails - Navigation handler function
 * @returns {Object} DataTable formatted object with columns and rows
 */
export const transformMerchantsToTableData = (merchants, columns, navigateToMerchantDetails) => {
    const rows = merchants.map((merchant, index) => transformMerchantItem(merchant, navigateToMerchantDetails, index));

    return {
        columns,
        rows
    };
};

/**
 * Transforms array of uninstalled merchants into DataTable format
 * @param {Array} merchants - Array of merchant objects from API
 * @param {Array} columns - Table column configuration
 * @param {Function} navigateToMerchantDetails - Navigation handler function
 * @returns {Object} DataTable formatted object with columns and rows
 */
export const transformUninstalledMerchantsToTableData = (merchants, columns, navigateToMerchantDetails) => {
    const rows = merchants.map(merchant => transformUninstalledMerchantItem(merchant, navigateToMerchantDetails));

    return {
        columns,
        rows
    };
};

